import { Trans, useTranslation } from 'next-i18next/pages';
import { useMemo } from 'react';

import { JobTitle } from '@pflegia/data-access';
import { FunnelQuestionWithPosition, useMultipleVariantData } from '@pflegia/feature-flags';
import { FunnelQuestion } from '@pflegia/types/api/funnel/questions';
import { JobTitleEnum } from '@pflegia/types/api/job-title/job-titles.enum';
import { RequiredJobTitle } from '@pflegia/types/api/job-title/multiposting/types';
import { getSelectedJobTitleFromAnswers } from '@pflegia/ui/funnel/utils/job-titles';
import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import useJobTitle from '@pflegia/ui/shared/job-title/hooks/use-job-title';
import { isPraxiaJobTitle } from '@pflegia/util/pflegia/job-title/is-praxia-job-title';

import { useFunnelState } from '@domains/funnel/hooks/use-funnel-state';

import useRegisterMethod from '@shared/hooks/use-register-method';

export const QUESTIONS_TO_APPLY_FEATURE_FLAG = [FunnelQuestion.JobYearsOfExperience, FunnelQuestion.YearsOfExperience];

const QuestionHeader: React.FC<{ question: FunnelQuestion }> = ({ question }) => {
  const { t } = useTranslation('funnel', { keyPrefix: 'questions' });
  const { translateJobTitle } = useJobTitle();
  const jobTitleAnswers = useFunnelState(state => state[FunnelQuestion.PositionDesired]);

  const selectedJobTitle = jobTitleAnswers?.length ? getSelectedJobTitleFromAnswers(jobTitleAnswers, question) : undefined;
  const { isPraxia } = useFlavorFromRoute();
  const { payload: funnelQuestionWithTitle } = useMultipleVariantData(FunnelQuestionWithPosition.Name);
  const { isMultipostingFunnel, isTransparentFunnel } = useRegisterMethod();
  const shouldShowPyhsiotherapyHeader =
    Boolean(selectedJobTitle) &&
    (selectedJobTitle as RequiredJobTitle).title === JobTitleEnum.PhysicalTherapist &&
    question === FunnelQuestion.YearsOfExperience;

  const i18nKey = useMemo(() => {
    if (
      Boolean(selectedJobTitle) &&
      isPraxiaJobTitle(selectedJobTitle as RequiredJobTitle) &&
      (isMultipostingFunnel || isTransparentFunnel) &&
      question === FunnelQuestion.YearsOfExperience
    ) {
      return 'YearsOfExperiencePraxia';
    }

    if (
      !isPraxia &&
      QUESTIONS_TO_APPLY_FEATURE_FLAG.includes(question) &&
      funnelQuestionWithTitle &&
      shouldShowPyhsiotherapyHeader === false
    ) {
      return 'OpenYearsOfExperience';
    }

    if (shouldShowPyhsiotherapyHeader) return 'JobYearsOfExperience';

    return question;
  }, [
    selectedJobTitle,
    isMultipostingFunnel,
    isTransparentFunnel,
    question,
    isPraxia,
    funnelQuestionWithTitle,
    shouldShowPyhsiotherapyHeader,
  ]);
  return (
    <Trans
      // eslint-disable-next-line id-length
      components={{ u: <u className='underline underline-offset-4' />, highlight: <span className='text-main-blue' /> }}
      i18nKey={i18nKey}
      t={t}
      values={{ jobTitle: translateJobTitle(selectedJobTitle as JobTitle, true) }}
    />
  );
};

export default QuestionHeader;
